import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Switch,
  FormControlLabel,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { agentApi } from '../../utils/agentApi';

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

interface WhatsAppOficialModalProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  onSuccess?: () => void;
}

const APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID;
const CONFIG_ID = process.env.REACT_APP_FACEBOOK_APP_CONFIG_ID;

interface ConnectionPreviewData {
  wabaId: string;
  phoneNumberId: string;
  phoneNumber: string | null;
  verifiedName: string | null;
  status: string | null;
  codeVerificationStatus: string | null;
  token: string;
}

const ConnectWhatsAppOficialModal: React.FC<WhatsAppOficialModalProps> = ({
  open,
  onClose,
  agentId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [previewData, setPreviewData] = useState<ConnectionPreviewData | null>(null);
  const [tokenCode, setTokenCode] = useState<string | null>(null);
  const [acceptedMetaCharges, setAcceptedMetaCharges] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize Facebook SDK
  useEffect(() => {
    const initFB = () => {
      if (window.FB) {
        // Check if already initialized
        try {
          const appId = window.FB.getAppId();
          if (!appId || appId !== APP_ID) {
            window.FB.init({
              appId: APP_ID,
              cookie: true,
              xfbml: true,
              version: 'v24.0'
            });
          }
        } catch (e) {
          // FB not initialized, initialize it
          window.FB.init({
            appId: APP_ID,
            cookie: true,
            xfbml: true,
            version: 'v24.0'
          });
        }
      } else {
        // SDK not loaded yet, set up async init
        if (!window.fbAsyncInit) {
          window.fbAsyncInit = function() {
            if (window.FB) {
              window.FB.init({
                appId: APP_ID,
                cookie: true,
                xfbml: true,
                version: 'v24.0'
              });
            }
          };
        }
      }
    };

    // Try to initialize immediately if SDK is already loaded
    if (window.FB) {
      initFB();
    } else {
      // Wait for SDK to load
      const checkInterval = setInterval(() => {
        if (window.FB) {
          initFB();
          clearInterval(checkInterval);
        }
      }, 100);

      // Cleanup after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);
    }
  }, []);

  // Set up message event listener for WA_EMBEDDED_SIGNUP messages
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (!event.origin.includes('facebook.com')) return;
      
      try {
        const messageData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        if (messageData.type === 'WA_EMBEDDED_SIGNUP') {
          const embeddedData = messageData.data;
          const wabaId = embeddedData?.waba_id || embeddedData?.wabaId;
          const phoneNumberId = embeddedData?.phone_number_id || embeddedData?.phoneNumberId || embeddedData?.business_phone_number_id;
          
          if (wabaId && phoneNumberId) {
            const dataToStore = {
              waba_id: wabaId,
              business_phone_number_id: phoneNumberId
            };
            
            console.log('💾 Storing embedded signup data:', dataToStore);
            (window as any).__waEmbeddedSignupData = dataToStore;
          } else {
            console.warn('⚠️ Could not extract IDs from embedded data:', embeddedData);
          }
        }
      } catch (e) {
        console.log('ℹ️ Message event data (not JSON):', event.data);
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Launch Embedded Signup when modal opens
  useEffect(() => {
    if (!open) {
      return;
    }

    setError(null);
    setSuccess(false);
    setPreviewData(null);
    setTokenCode(null);
    setAcceptedMetaCharges(false);
    
    // Clear any stored data
    (window as any).__waEmbeddedSignupData = null;

  }, [open]);

  // Launch WhatsApp Signup function
  const launchWhatsAppSignup = () => {
    if (!window.FB) {
      setError(t('agents.whatsapp.connectOfficialModal.errors.sdkNotLoaded'));
      return;
    }

    // Verify FB is initialized
    try {
      const appId = window.FB.getAppId();
      if (!appId || appId !== APP_ID) {
        window.FB.init({
          appId: APP_ID,
          cookie: true,
          xfbml: true,
          version: 'v24.0'
        });
      }
    } catch (e) {
      window.FB.init({
        appId: APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v24.0'
      });
    }

    // Facebook login callback (must be synchronous)
    const fbLoginCallback = (response: any) => {
      if (response.authResponse) {
        const tokenCode = response.authResponse.code;
        
        // Get stored waba_id and business_phone_number_id from message event
        const storedData = (window as any).__waEmbeddedSignupData;
        
        // Process async operation inside a separate async function
        const processPreview = async () => {
          if (tokenCode && storedData && storedData.waba_id && storedData.business_phone_number_id) {
            setLoading(true);
            setError(null);

            try {
              // Preview connection data before confirming
              const preview = await agentApi.previewWhatsAppOficialConnection({
                wabaId: storedData.waba_id,
                phoneNumberId: storedData.business_phone_number_id,
                tokenCode: tokenCode,
              });

              // Store preview data and token code for confirmation
              setPreviewData(preview);
              setTokenCode(tokenCode);
            } catch (err: any) {
              setError(err.message || t('agents.whatsapp.connectOfficialModal.errors.fetchConnectionData'));
            } finally {
              setLoading(false);
            }
          } else {
            // If we don't have the data yet, wait a bit and try again
            setTimeout(() => {
              const retryData = (window as any).__waEmbeddedSignupData;
              if (retryData && retryData.waba_id && retryData.business_phone_number_id) {
                processPreview();
              } else {
                setError(t('agents.whatsapp.connectOfficialModal.errors.incompleteData'));
              }
            }, 1000);
          }
        };

        // Call the async function
        processPreview();
      } else {
        console.log('FB Login response (no auth):', response);
        if (response.error) {
          setError(response.error.message || t('agents.whatsapp.connectOfficialModal.errors.facebookLogin'));
        }
      }
    };

    // Launch Facebook login with Embedded Signup configuration
    try {
      window.FB.login(fbLoginCallback, {
        config_id: CONFIG_ID,
        response_type: 'code', // must be set to 'code' for System User access token
        override_default_response_type: true, // when true, any response types passed in the "response_type" will take precedence over the default types
        auth_type: 'rerequest',
        extras: {
          version: 'v3'
        }
      });
    } catch (err: any) {
      console.error('Error launching FB.login:', err);
      setError(err.message || t('agents.whatsapp.connectOfficialModal.errors.startConnection'));
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccess(false);
      setPreviewData(null);
      setTokenCode(null);
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      onClose();
    }
  };

  const handleConfirmConnection = async () => {
    if (!previewData || !tokenCode) {
      setError(t('agents.whatsapp.connectOfficialModal.errors.connectionDataUnavailable'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await agentApi.connectWhatsAppOficial(agentId, {
        wabaId: previewData.wabaId,
        phoneNumberId: previewData.phoneNumberId,
        tokenCode: previewData.token,
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || t('agents.whatsapp.connectOfficialModal.errors.processConnection'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPreview = () => {
    setPreviewData(null);
    setTokenCode(null);
    setError(null);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: 400,
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
          </svg>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('agents.whatsapp.connectOfficialModal.title')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={loading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('agents.whatsapp.connectOfficialModal.successMessage')}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>
              {previewData ? t('agents.whatsapp.connectOfficialModal.confirmingConnection') : t('agents.whatsapp.connectOfficialModal.processingConnection')}
            </Typography>
          </Box>
        ) : previewData ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              py: 2,
            }}
          >
            <Alert severity="success">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                {t('agents.whatsapp.connectOfficialModal.connectionDataReceived')}
              </Typography>
              
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  p: 3,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 2,
                  border: '2px solid #e9ecef',
                  mb: 2,
                  gap: 1,
                  maxWidth: '500px',
                  mx: 'auto'
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 600 }}>
                    {t('agents.whatsapp.connectOfficialModal.wabaId')}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      fontFamily: 'monospace',
                      textAlign: 'center',
                      backgroundColor: 'white',
                      padding: '12px 24px',
                      borderRadius: 1,
                      border: '2px solid #28a745',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      color: '#28a745',
                      wordBreak: 'break-all'
                    }}
                  >
                    {previewData.wabaId}
                  </Typography>
                </Box>

                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 600 }}>
                    {t('agents.whatsapp.connectOfficialModal.phoneNumber')}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      fontFamily: 'monospace',
                      textAlign: 'center',
                      backgroundColor: 'white',
                      padding: '12px 24px',
                      borderRadius: 1,
                      border: '2px solid #28a745',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      color: '#28a745'
                    }}
                  >
                    {previewData.phoneNumber || t('agents.whatsapp.connectOfficialModal.phoneNotAvailable')}
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('agents.whatsapp.connectOfficialModal.reviewData')}
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancelPreview}
                disabled={loading}
                fullWidth
              >
                {t('agents.whatsapp.connectOfficialModal.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmConnection}
                disabled={loading}
                fullWidth
                sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined
                }}
              >
                {t('agents.whatsapp.connectOfficialModal.confirmConnection')}
              </Button>
            </Box>
          </Box>
        ) : !success ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 2,
              gap: 2,
              height: '100%',
            }}
          >
            <Typography variant="body1" color="text.secondary" textAlign="center">
              {t('agents.whatsapp.connectOfficialModal.initialInstruction')}
            </Typography>
            
            <Box
              sx={{
                width: '100%',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                bgcolor: 'background.paper',
                mb: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={acceptedMetaCharges}
                    onChange={(e) => setAcceptedMetaCharges(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    {t('agents.whatsapp.connectOfficialModal.acceptCharges')}
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>

            <Button
              variant="contained"
              onClick={launchWhatsAppSignup}
              disabled={loading || !acceptedMetaCharges}
              sx={{
                backgroundColor: '#1877f2',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                },
                minWidth: 200,
                height: 40,
                fontWeight: 'bold',
              }}
            >
              {t('agents.whatsapp.connectOfficialModal.loginWithFacebook')}
            </Button>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={loading}>
          {success ? t('agents.whatsapp.connectOfficialModal.close') : t('agents.whatsapp.connectOfficialModal.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectWhatsAppOficialModal;